import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import { BiBell, BiBoltCircle, BiChip, BiEnvelope, BiFlag, BiGlobe, BiGroup, BiListUl, BiShield, BiTimeFive, BiTime } from 'react-icons/bi';
import WorkspacePageHeader from '../../../components/workspace/WorkspacePageHeader';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useWorkspaceAccess } from '../../../hooks/useWorkspaceAccess';
import { MASTERS_ACCESS, SETTINGS_ACCESS } from '../../../config/workspaceAccess';
import { isWorkspaceAdmin } from '../../../utils/permissions';
import { settingsBase } from './settingsPaths';
import '../tender/MastersPage.scss';

const AppSettingsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const base = settingsBase(location.pathname);
  const user = useSelector((state) => state.auth.user);
  const admin = isWorkspaceAdmin(user?.roles);
  const { ready, allowed } = useWorkspaceAccess();

  useDocumentTitle('Settings');

  const cards = [
    {
      id: 'organisation',
      title: 'Organisation',
      description: 'Currency, calendar, personnel types, import values, plan limits, and the other organisation rules.',
      icon: BiGlobe,
      to: `${base}/organisation`,
      allow: SETTINGS_ACCESS,
      admins: true
    },
    {
      id: 'users',
      title: 'User management',
      description: 'Invite people, assign roles, and turn an account on or off.',
      icon: BiGroup,
      to: `${base}/users`,
      allow: ['platform.users.manage', 'users.view'],
      admins: true
    },
    {
      id: 'roles',
      title: 'Roles and permissions',
      description: 'Choose what each role can open. A person receives the permissions of every role they hold.',
      icon: BiShield,
      to: `${base}/roles`,
      allow: ['platform.roles.manage', 'roles.view'],
      admins: true
    },
    {
      id: 'email',
      title: 'Email',
      description: 'Connect the mailbox, test the connection, and send a trial message.',
      icon: BiEnvelope,
      to: `${base}/email`,
      allow: MASTERS_ACCESS,
      admins: true
    },
    {
      id: 'notifications',
      title: 'Notifications',
      description: 'Choose whether the organisation may send email notices.',
      icon: BiBell,
      to: `${base}/notifications`,
      allow: ['platform.company.manage', 'company.view'],
      admins: true
    },
    {
      id: 'ai',
      title: 'AI',
      description: 'Choose Gemini or OpenAI. The key is encrypted and shown only as XXXXXXXXXXXX.',
      icon: BiChip,
      to: `${base}/ai`,
      allow: ['platform.ai.configure', 'ai.view'],
      admins: true
    },
    {
      id: 'tender-stages',
      title: 'Tender stages',
      description: 'The stage list used when a tender is created or edited.',
      icon: BiFlag,
      to: `${base}/dropdowns/tender-stages`,
      allow: MASTERS_ACCESS,
      admins: true
    },
    {
      id: 'tender-priority',
      title: 'Priority',
      description: 'How important a pursuit is. Active values appear on the tender form.',
      icon: BiBoltCircle,
      to: `${base}/dropdowns/tender-priority`,
      allow: MASTERS_ACCESS,
      admins: true
    },
    {
      id: 'tender-urgency',
      title: 'Urgency',
      description: 'How soon a pursuit needs attention. Active values appear on the tender form.',
      icon: BiTimeFive,
      to: `${base}/dropdowns/tender-urgency`,
      allow: MASTERS_ACCESS,
      admins: true
    },
    {
      id: 'dropdowns',
      title: 'Dropdown master',
      description: 'All of the lists used on forms, including stages, priority, and urgency.',
      icon: BiListUl,
      to: `${base}/dropdowns`,
      allow: MASTERS_ACCESS,
      admins: true
    },
    {
      id: 'pursuit-rules',
      title: 'Pursuit reminders',
      description: 'Lead times for BG/EMD expiry, release follow-up, and pre-bid query deadlines.',
      icon: BiTime,
      to: `${base}/pursuit`,
      allow: MASTERS_ACCESS,
      admins: true
    },
    {
      id: 'timesheet-rules',
      title: 'Timesheet rules',
      description: 'Daily limit, hour steps, date window, approver fallback, and who may reopen approved time.',
      icon: BiTime,
      to: `${base}/timesheet`,
      allow: MASTERS_ACCESS,
      admins: true
    },
  ].filter((card) => allowed(card.allow) || (card.admins && admin));

  return (
    <div className="mst-catalog">
      <WorkspacePageHeader
        title="Settings"
        help="settings"
        subtitle="Who can sign in, what each role may do, and the lists that shape how a tender is captured."
      />
      {!ready ? <div className="py-4 text-center"><Spinner animation="border" size="sm" /></div> : (
      <div className="mst-catalog__grid">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <button
              key={card.id}
              type="button"
              className="mst-catalog__card"
              onClick={() => navigate(card.to)}
            >
              <div className="mst-catalog__card-head">
                <span className="mst-catalog__icon" aria-hidden>
                  <Icon />
                </span>
                <h2>{card.title}</h2>
              </div>
              <p>{card.description}</p>
            </button>
          );
        })}
      </div>
      )}
    </div>
  );
};

export default AppSettingsPage;
